'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useProjects, useProject } from './use-projects';
import { useLastProject } from '@/shared/hooks/use-last-project';

/**
 * The building currently addressed by the URL — any `/project/{id}/...` route
 * (building home, concept, sub-pages, deep links from Recent). This is the
 * single "switch" signal for the active building.
 */
export function useUrlProjectId(): string | null {
  const pathname = usePathname();
  return useMemo(() => {
    const match = pathname.match(/\/project\/([^/]+)/);
    return match?.[1] ?? null;
  }, [pathname]);
}

/**
 * The active building, resolved IDENTICALLY for every navigation surface
 * (desktop sidebar + floating bottom bar): URL building → last-opened building
 * → first project.
 *
 * Persistence of `lastProjectId` does NOT live here — it is owned by
 * `<ActiveProjectSync/>`, mounted once in the authed layout. That separation is
 * the whole point: the desktop sidebar only mounts on wide+expanded screens, so
 * tying persistence to it meant nothing was ever stored on narrow screens or
 * with the sidebar collapsed, and the two navs disagreed on the active building
 * (the bottom bar fell back to `projects[0]`). A single always-mounted writer +
 * this shared reader keep both navs in lockstep on every viewport.
 */
export function useActiveProjectId(): string | null {
  const urlProjectId = useUrlProjectId();
  const { lastProjectId } = useLastProject();
  const { data: projectsResponse } = useProjects();
  return (
    urlProjectId || lastProjectId || projectsResponse?.data?.[0]?.id || null
  );
}

/** Active building resource (name etc.) for display in nav surfaces. */
export function useActiveProject() {
  const activeProjectId = useActiveProjectId();
  const { data } = useProject(activeProjectId || '', {
    enabled: !!activeProjectId,
  });
  return { activeProjectId, activeProject: data?.data ?? null };
}
